import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { accessControlService } from '../services/accessControlService'
import { ConfidentialityLevel } from '../types/confidentiality'
import { EmergencyOverrideModal } from './EmergencyOverrideModal'

interface MaskedFieldProps {
  field: string
  value: any
  patientId?: string
  confidentialityLevel?: ConfidentialityLevel
  maskType?: 'full' | 'partial' | 'metadata-only'
  children?: (canAccess: boolean, maskedValue: any, requestEmergencyAccess?: () => void) => React.ReactNode
  renderRestricted?: () => React.ReactNode
  className?: string
}

/**
 * MaskedField component for displaying patient data with field-level access control.
 * 
 * This component checks user permissions and applies masking based on:
 * - User's field-level permissions (e.g., patients:read:medications)
 * - Patient/field confidentiality levels
 * - Active emergency overrides
 * 
 * @example
 * ```tsx
 * <MaskedField field="medications" value={patient.currentMedications} patientId={patient.id}>
 *   {(canAccess, maskedValue) => (
 *     canAccess ? (
 *       <MedicationsList medications={maskedValue} />
 *     ) : (
 *       <RestrictedAccessMessage />
 *     )
 *   )}
 * </MaskedField>
 * ```
 */
export const MaskedField: React.FC<MaskedFieldProps> = ({
  field,
  value,
  patientId,
  confidentialityLevel,
  maskType = 'partial',
  children,
  renderRestricted,
  className,
}) => {
  const { hasFieldPermission, canOverrideAccess } = useAuth()
  const [canAccess, setCanAccess] = React.useState<boolean>(false)
  const [maskedValue, setMaskedValue] = React.useState<any>(value)
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [activeOverride, setActiveOverride] = React.useState<any>(null)
  const [showOverrideModal, setShowOverrideModal] = useState(false)

  React.useEffect(() => {
    async function checkAccess() {
      setIsLoading(true)
      
      try {
        // Check for active override first (before checking permissions)
        let hasActiveOverride = false
        let override = null
        if (patientId) {
          override = await accessControlService.getActiveOverride([field], patientId)
          setActiveOverride(override)
          if (override && accessControlService.isOverrideActive(override)) {
            hasActiveOverride = true
          }
        }
        
        // Check access via access control service
        const accessResult = hasActiveOverride || accessControlService.canAccessField(
          field,
          patientId,
          confidentialityLevel
        )
        
        setCanAccess(accessResult)
        
        // Get masked value if access is denied
        if (!accessResult) {
          const masked = accessControlService.getMaskedValue(field, value, maskType)
          setMaskedValue(masked)
        } else {
          setMaskedValue(value)
        }
      } catch (error) {
        console.error('Error checking field access:', error)
        // On error, default to restricted access
        setCanAccess(false)
        setMaskedValue(accessControlService.getMaskedValue(field, value, maskType))
      } finally {
        setIsLoading(false)
      }
    }

    checkAccess()
  }, [field, value, patientId, confidentialityLevel, maskType, hasFieldPermission])

  if (isLoading) {
    return (
      <div className={className} aria-live="polite" aria-busy="true">
        <span className="sr-only">Checking access permissions...</span>
        <div className="text-gray-500 text-sm">Loading...</div>
      </div>
    )
  }

  // If children is a render function, use it
  if (children) {
    const hasOverridePermission = canOverrideAccess()
    console.log('[MaskedField] Rendering with children, hasOverridePermission:', hasOverridePermission, 'patientId:', patientId, 'showOverrideModal:', showOverrideModal)
    
    // Always provide the callback if patientId exists - let the modal handle permission checks
    const requestEmergencyAccess = patientId 
      ? () => {
          console.log('[MaskedField] Opening emergency override modal for field:', field, 'patientId:', patientId, 'hasOverridePermission:', hasOverridePermission, 'current showOverrideModal state:', showOverrideModal)
          setShowOverrideModal(true)
          console.log('[MaskedField] After setShowOverrideModal(true), state should be updated')
        }
      : undefined
    
    return (
      <>
        {children(canAccess, maskedValue, requestEmergencyAccess)}
        {patientId && (
          <EmergencyOverrideModal
            isOpen={showOverrideModal}
            onClose={() => {
              console.log('[MaskedField] Closing emergency override modal')
              setShowOverrideModal(false)
            }}
            patientId={patientId}
            fields={[field]}
            onSuccess={async () => {
              // Refetch access after override is requested
              setIsLoading(true)
              try {
                // Re-check for active override
                const override = await accessControlService.getActiveOverride([field], patientId)
                setActiveOverride(override)
                if (override && accessControlService.isOverrideActive(override)) {
                  setCanAccess(true)
                  setMaskedValue(value)
                }
              } catch (error) {
                console.error('Error refreshing access after override request:', error)
              } finally {
                setIsLoading(false)
              }
            }}
          />
        )}
      </>
    )
  }

  // If access is denied, show restricted message
  if (!canAccess) {
    if (renderRestricted) {
      return (
        <>
          {renderRestricted()}
          {patientId && (
            <EmergencyOverrideModal
              isOpen={showOverrideModal}
              onClose={() => setShowOverrideModal(false)}
              patientId={patientId}
              fields={[field]}
              onSuccess={async () => {
                setIsLoading(true)
                try {
                  const override = await accessControlService.getActiveOverride([field], patientId)
                  setActiveOverride(override)
                  if (override && accessControlService.isOverrideActive(override)) {
                    setCanAccess(true)
                    setMaskedValue(value)
                  }
                } catch (error) {
                  console.error('Error refreshing access after override request:', error)
                } finally {
                  setIsLoading(false)
                }
              }}
            />
          )}
        </>
      )
    }

    return (
      <>
        <div className={className} role="alert" aria-live="polite">
          <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
            <div className="flex items-center">
              <svg
                className="h-5 w-5 text-gray-400 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-900">Restricted Access</h3>
                <p className="text-sm text-gray-600 mt-1">
                  You do not have permission to view this information.
                </p>
                {patientId && (
                  <button
                    onClick={() => {
                      console.log('[MaskedField] Default render - Opening emergency override modal')
                      setShowOverrideModal(true)
                    }}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Request Emergency Access
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        {patientId && (
          <EmergencyOverrideModal
            isOpen={showOverrideModal}
            onClose={() => setShowOverrideModal(false)}
            patientId={patientId}
            fields={[field]}
            onSuccess={async () => {
              setIsLoading(true)
              try {
                const override = await accessControlService.getActiveOverride([field], patientId)
                setActiveOverride(override)
                if (override && accessControlService.isOverrideActive(override)) {
                  setCanAccess(true)
                  setMaskedValue(value)
                }
              } catch (error) {
                console.error('Error refreshing access after override request:', error)
              } finally {
                setIsLoading(false)
              }
            }}
          />
        )}
      </>
    )
  }

  // Access granted - show value
  return (
    <>
      <div className={className}>
        {activeOverride && (
          <div className="mb-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            <span className="font-medium">Emergency Override Active</span>
            {activeOverride.expiresAt && (
              <span className="ml-2">
                (Expires: {new Date(activeOverride.expiresAt).toLocaleString()})
              </span>
            )}
          </div>
        )}
        {typeof maskedValue === 'object' && maskedValue !== null ? (
          <pre className="text-sm">{JSON.stringify(maskedValue, null, 2)}</pre>
        ) : (
          <span>{maskedValue}</span>
        )}
      </div>
      {patientId && (
        <EmergencyOverrideModal
          isOpen={showOverrideModal}
          onClose={() => setShowOverrideModal(false)}
          patientId={patientId}
          fields={[field]}
          onSuccess={() => {
            // Refetch access after override is requested
            setIsLoading(true)
          }}
        />
      )}
    </>
  )
}

export default MaskedField
